import { Link } from 'react-router-dom';
import { usePageTitle } from '@/shared/hooks/usePageTitle';

import { routePaths } from '@/app/router/routePaths';
import { MatchRow } from '@/features/matches/MatchRow';
import {
  MATCH_STATUS_LABELS,
  describeRules,
  matchesOfTeam,
  nextMatchFor,
  sideLabel,
} from '@/features/matches/matchPresentation';
import { StandingsTableView } from '@/features/standings/StandingsTableView';
import { useTournamentData } from '@/features/tournament/useTournamentData';
import { Alert } from '@/shared/ui/Alert';
import { LoadingScreen } from '@/shared/ui/LoadingScreen';
import { PageState } from '@/shared/ui/PageState';

export function DashboardPage() {
  usePageTitle('Dashboard');
  const { tournament, myTeam, matches, lookup, rulesFor, phases, isLoading, errorMessage } =
    useTournamentData();

  if (isLoading) {
    return <LoadingScreen label="Turnierdaten werden geladen …" />;
  }

  if (errorMessage !== null) {
    return <PageState title="Turnierdaten nicht verfügbar" description={errorMessage} />;
  }

  if (myTeam === null) {
    return (
      <PageState
        title="Noch kein Team angemeldet"
        description="Melde dein Team an, um Spielplan und Ergebnisse zu sehen."
        action={
          <Link
            to={routePaths.team.registration}
            className="bg-brand hover:bg-brand-hover text-brand-fg touch-target rounded-lg px-5 py-3 font-medium transition-colors"
          >
            Team anmelden
          </Link>
        }
      />
    );
  }

  const ownMatches = matchesOfTeam(matches, myTeam.id);
  const next = nextMatchFor(matches, myTeam.id);
  const ownGroup = phases
    .flatMap((phase) => phase.standings)
    .find((table) => table.rows.some((row) => row.teamId === myTeam.id));

  return (
    <div className="flex flex-col gap-8">
      <header className="flex flex-col gap-1">
        <p className="text-brand text-xs font-semibold tracking-widest uppercase">
          {myTeam.fireDepartment}
        </p>
        <h1 className="text-2xl font-bold tracking-tight">{myTeam.name}</h1>
        {tournament !== undefined && (
          <p className="text-fg-muted text-sm">{tournament.settings.general.name}</p>
        )}
      </header>

      {matches.length === 0 ? (
        <Alert tone="info">
          Das Turnier wurde noch nicht gestartet. Sobald die Turnierleitung den Spielplan erstellt,
          erscheint er hier automatisch.
        </Alert>
      ) : (
        <>
          <NextMatchCard match={next} lookup={lookup} rulesFor={rulesFor} ownTeamId={myTeam.id} />

          {ownGroup !== undefined && (
            <section className="flex flex-col gap-3">
              <h2 className="text-lg font-semibold">Gruppenstand</h2>
              <StandingsTableView table={ownGroup} lookup={lookup} highlightTeamId={myTeam.id} />
            </section>
          )}

          <section className="flex flex-col gap-3">
            <div className="flex items-baseline justify-between gap-4">
              <h2 className="text-lg font-semibold">Eigene Spiele</h2>
              <Link to={routePaths.tournament.schedule} className="text-fg-muted text-sm underline">
                Gesamter Spielplan
              </Link>
            </div>
            <ul className="flex flex-col gap-2">
              {ownMatches.map((match) => (
                <MatchRow
                  key={match.id}
                  match={match}
                  lookup={lookup}
                  rules={rulesFor(match)}
                  highlightTeamId={myTeam.id}
                  to={routePaths.tournament.match(match.id)}
                />
              ))}
            </ul>
          </section>
        </>
      )}
    </div>
  );
}

interface NextMatchCardProps {
  match: ReturnType<typeof nextMatchFor>;
  lookup: ReturnType<typeof useTournamentData>['lookup'];
  rulesFor: ReturnType<typeof useTournamentData>['rulesFor'];
  ownTeamId: string;
}

function NextMatchCard({ match, lookup, rulesFor, ownTeamId }: NextMatchCardProps) {
  if (match === undefined) {
    return (
      <section className="border-line bg-surface rounded-xl border p-5">
        <h2 className="text-fg-subtle text-xs tracking-widest uppercase">Nächstes Spiel</h2>
        <p className="text-fg-muted mt-2 text-sm">Derzeit ist kein weiteres Spiel angesetzt.</p>
      </section>
    );
  }

  const opponent =
    match.home.kind === 'team' && match.home.teamId === ownTeamId ? match.away : match.home;

  return (
    <Link
      to={routePaths.tournament.match(match.id)}
      className="border-brand/40 bg-brand-subtle hover:border-brand block rounded-xl border p-5 transition-colors"
    >
      <h2 className="text-fg-subtle text-xs tracking-widest uppercase">Nächstes Spiel</h2>

      <p className="mt-2 text-xl font-bold tracking-tight">gegen {sideLabel(opponent, lookup)}</p>

      <dl className="text-fg-muted mt-4 flex flex-wrap gap-x-8 gap-y-2 text-sm">
        <div>
          <dt className="text-fg-subtle text-xs uppercase">Board</dt>
          <dd className="text-fg font-semibold">{match.boardNumber ?? 'noch offen'}</dd>
        </div>
        <div>
          <dt className="text-fg-subtle text-xs uppercase">Status</dt>
          <dd className="text-fg font-semibold">{MATCH_STATUS_LABELS[match.status]}</dd>
        </div>
        <div>
          <dt className="text-fg-subtle text-xs uppercase">Modus</dt>
          <dd className="text-fg font-semibold">{describeRules(rulesFor(match))}</dd>
        </div>
      </dl>

      <p className="text-brand mt-4 text-sm font-medium">Ergebnis melden →</p>
    </Link>
  );
}
